import React, { useContext, useEffect, useRef, useState } from 'react'
import { Row, Select, TextField } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getEntity } from '../../util/EntityUtil'
import { RelationshipCalculatorContext } from './RelationshipCalculatorContext'
import { getUserMetadata } from '../../services/userService'
import { getPerson } from '../../services/personService'

const PERSON_PREFIX = 'p.'
const USER_PREFIX = 'u.'
const CIS_USER_PREFIX = 'cis.'
const CIS_USER_PREFIX_2 = 'cis.user.'

// Before Zion v11 translations were pre-loaded in the app, so the translations were always available.
// That was bad for performance and bundle sizes. With Zion v11, only the language we are using is
// loaded. That is done asynchronously, so top level translations are blank and t() functions
// should be always called during rendering for proper result.
const PREFIX_VALIDATOR = {
  regEx:
    /^(?:(?:\d+)|(?:p|u)\.(?:\d+|[a-zA-Z0-9]{4}-?[a-zA-Z0-9]{3}))|(?:mrn\.(?:\d{3}00\d{8}|\d{3}-\d{4}-\d{4}))|(?:cis\.(?:user.)?(?:\d+|[a-zA-Z0-9]{4}-?[a-zA-Z0-9]{4}))$/i,
}

const IS_PURE_NUMBER = /^\d+$/

const getEntityLength = (prefix) => {
  let encodeTotalCharacters = 0
  if (/(?:p|u)\./.test(prefix)) encodeTotalCharacters = 7
  else if (prefix === CIS_USER_PREFIX) encodeTotalCharacters = 8
  return encodeTotalCharacters
}

const SelectId = (props) => {
  const { leftComponent, buttonCallback } = props
  const context = useContext(RelationshipCalculatorContext)
  const { insetInlineStart, insetInlineEnd } = context
  const side = leftComponent ? insetInlineStart : insetInlineEnd

  const { t } = useTranslation()

  const [prefix, setPrefix] = useState(side.prefix === '' ? PERSON_PREFIX : side.prefix.concat('.'))
  const [idValue, setIdValue] = useState(side.inputId)
  const idValueRef = useRef(idValue)
  const [validLabel, setValidLabel] = useState(side.label === '' ? null : true)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const PREFIX_OPTIONS = [
    {
      value: PERSON_PREFIX,
      label: t('personId', 'Person ID'),
    },
    {
      value: USER_PREFIX,
      label: t('userId', 'User ID'),
    },
    {
      value: CIS_USER_PREFIX,
      label: t('cisUserId', 'CIS User ID'),
    },
  ]

  const setUserMetadata = (metadata) => {
    const oldMetadata = metadata
    const items = []
    if (oldMetadata.contributorId) {
      items.push({
        ...oldMetadata.contributorId,
        label: t('contributorId', 'Contributor ID'),
        showLabel: true,
        field: 'contributorId',
      })
    }
    if (oldMetadata.pid) {
      items.push({ ...oldMetadata.pid, label: t('pid', 'PID'), showLabel: true, field: 'pid' })
    }
    if (oldMetadata.mrn) {
      items.push({ ...oldMetadata.mrn, label: t('mrn', 'MRN'), showLabel: true, field: 'mrn' })
    }
    if (oldMetadata.lifeSpan) {
      items.push({ ...oldMetadata.lifeSpan, label: t('lifespan', 'Lifespan'), showLabel: false, field: 'lifeSpan' })
    }

    metadata = { header: oldMetadata.header, items, badges: null }
    return metadata
  }

  const setPersonMetadata = (oldMetadata) => {
    const items = []
    if (oldMetadata.lifeSpan) {
      items.push({ ...oldMetadata.lifeSpan, label: t('lifespan', 'Lifespan'), showLabel: false, field: 'lifeSpan' })
    }
    if (oldMetadata.ids) {
      items.push({ ...oldMetadata.ids, label: t('id', 'ID'), showLabel: false, field: 'ids' })
    }
    if (oldMetadata.cisId) {
      items.push({ ...oldMetadata.cisId, label: t('cisId', 'CIS ID'), showLabel: true, field: 'cisId' })
    }
    if (oldMetadata.contributorId) {
      items.push({
        ...oldMetadata.contributorId,
        label: t('contributorId', 'Contributor ID'),
        showLabel: true,
        field: 'contributorId',
      })
    }
    if (oldMetadata.mrn) {
      items.push({ ...oldMetadata.mrn, label: t('mrn', 'MRN'), showLabel: true, field: 'mrn' })
    }

    const badges = []
    if (oldMetadata.tombstonedBadge) {
      badges.push({ ...oldMetadata.tombstonedBadge, field: 'tombstoned' })
    }
    if (oldMetadata.ownerBadge) {
      badges.push({ ...oldMetadata.ownerBadge, field: 'ownerCisId' })
    }
    if (oldMetadata.forwardsToBadge) {
      badges.push({ ...oldMetadata.forwardsToBadge, field: 'forwardsTo' })
    }
    if (oldMetadata.forwardedFromBadge) {
      badges.push({ ...oldMetadata.forwardedFromBadge, field: 'forwardedFromBadge' })
    }

    return { header: oldMetadata.header, items, badges }
  }

  const validatePrefix = (inputPrefix) => {
    const processPrefix = inputPrefix.toLowerCase().concat('.')
    const validPrefix = PREFIX_OPTIONS.filter((item) => item.value === processPrefix)

    if (validPrefix.length > 0) return validPrefix[0].value

    // Otherwise it was an invalid inputPrefix - so return the existing prefix
    return prefix
  }

  const setContext = (metadata, enteredID) => {
    if (!isMountedRef.current) return

    if (typeof metadata !== 'undefined') {
      side.prefix = prefix
      side.inputId = enteredID
      // The User metadata uses PID and the Person metadata uses IDs
      // The Person metadata IDs format is "XXXX-XXX (999999)"
      // The User metadata PID format is "XXXX-XXX"
      side.personId = metadata.items
        ?.filter((item) => item.field === 'pid' || item.field === 'ids')[0]
        .value.value.split(' ')[0]
      // The User metadata has lifeSpan out of the array of items
      // The Person metadata has Lifespan in the array of items
      const lifeSpan =
        metadata.items?.filter((item) => item.field === 'lifeSpan')[0]?.value.value || metadata.lifeSpan?.value?.value
      if (lifeSpan !== null && lifeSpan !== undefined) {
        side.label = metadata.header.concat('(').concat(lifeSpan).concat(')')
      } else if (lifeSpan === undefined) {
        // Some people do NOT have any birth/death information - so label with the name only
        side.label = metadata.header
      } else {
        side.label = ''
      }
      setValidLabel(true)
      side.validId = true
      buttonCallback(insetInlineStart.validId, insetInlineEnd.validId)
    } else if (idValue !== '') {
      setValidLabel(false)
    }
  }

  const parseCisUserFormat = (splitValue) => {
    const prefixPart = `${splitValue[0]}.${splitValue[1]}`
    if (`${prefixPart}.` === CIS_USER_PREFIX_2) {
      return {
        isValid: true,
        prefix: CIS_USER_PREFIX,
        inputId: splitValue[2],
      }
    }
    return { isValid: false }
  }

  const parseIdInput = (value) => {
    const splitValue = value.split('.')

    // if there are more than 2 dots - I won't deal with it - just leave it alone
    if (splitValue.length > 3) {
      return { isValid: false, prefix: null, inputId: value }
    }

    if (splitValue.length === 3) {
      const result = parseCisUserFormat(splitValue)
      if (!result.isValid) {
        return { isValid: false, prefix: null, inputId: value }
      }
      return { ...result, hasExplicitPrefix: true }
    }

    const hasExplicitPrefix = splitValue.length > 1
    const newPrefix = hasExplicitPrefix ? validatePrefix(splitValue[0]) : prefix
    const newInputId = hasExplicitPrefix ? splitValue[splitValue.length - 1] : value

    return {
      isValid: true,
      prefix: newPrefix,
      inputId: newInputId,
      hasExplicitPrefix,
    }
  }

  const encodeIdIfNeeded = (inputId, prefixValue) => {
    if (IS_PURE_NUMBER.test(inputId)) {
      return inputId
    }

    const encodeTotalCharacters = getEntityLength(prefixValue)
    return getEntity(prefixValue.concat(inputId), encodeTotalCharacters).alphaNumericId
  }

  // Helper function: Fetch person metadata and process it
  const fetchPersonMetadata = async (entity) => {
    const response = await getPerson(entity, null)
    if (response?.metadata) {
      response.metadata = setPersonMetadata(response.metadata)
    }
    return response?.metadata
  }

  // Helper function: Fetch user metadata and process it
  const fetchUserMetadataByPrefix = async (entity, prefixValue) => {
    const prefixParam = prefixValue === USER_PREFIX ? '' : prefixValue.replace('.', '')
    const response = await getUserMetadata(entity, prefixParam)
    response.data = setUserMetadata(response.data)
    return response.data
  }

  // Helper function: Fetch and set appropriate metadata based on prefix
  const fetchAndSetMetadata = async (prefixValue, entity, originalValue) => {
    try {
      const metadata =
        prefixValue === PERSON_PREFIX
          ? await fetchPersonMetadata(entity)
          : await fetchUserMetadataByPrefix(entity, prefixValue)

      setContext(metadata, entity)
      return true
    } catch (e) {
      if (isMountedRef.current) {
        setIdValue(originalValue)
        setValidLabel(false)
      }
      return false
    }
  }

  // Helper function: Check if the full ID format is valid
  const isValidIdFormat = (prefixValue, inputId) => {
    return PREFIX_VALIDATOR.regEx.test(prefixValue.concat(inputId))
  }

  const onChangeValidateIdValue = async (event) => {
    event.preventDefault()
    const value = event.target.value

    // Parse and validate input structure
    const parsed = parseIdInput(value)

    // Early return for invalid formats
    if (!parsed.isValid) {
      setIdValue(value)
      setValidLabel(false)
      return
    }

    // Update prefix and ID value
    if (parsed.hasExplicitPrefix && parsed.prefix !== null) {
      setPrefix(parsed.prefix)
    }
    setIdValue(parsed.inputId)

    // Check if the complete ID format is valid
    if (!isValidIdFormat(parsed.prefix, parsed.inputId)) {
      setIdValue(parsed.inputId)
      setValidLabel(parsed.hasExplicitPrefix ? false : null)
      side.validId = false
      buttonCallback(insetInlineStart.validId, insetInlineEnd.validId)
      return
    }

    setPrefix(parsed.prefix)

    if (IS_PURE_NUMBER.test(parsed.inputId)) {
      return
    }

    const entity = encodeIdIfNeeded(parsed.inputId, parsed.prefix)
    setIdValue(entity)
    await fetchAndSetMetadata(parsed.prefix, entity, value)
  }

  const onClickHandler = () => {
    idValueRef.current.select()
  }

  const processNumericIdOnBlur = async (inputId, prefixValue, originalValue) => {
    const encodeTotalCharacters = getEntityLength(prefixValue)

    try {
      const encodedId = getEntity(inputId, encodeTotalCharacters).alphaNumericId
      setIdValue(encodedId)

      if (encodedId == null) {
        throw new Error('Id is null.')
      }

      const metadata =
        prefixValue === PERSON_PREFIX
          ? await fetchPersonMetadata(encodedId)
          : await fetchUserMetadataByPrefix(encodedId, prefixValue)

      setContext(metadata, encodedId)
      return true
    } catch (e) {
      if (isMountedRef.current) {
        setIdValue(originalValue)
        setValidLabel(false)
      }
      return false
    }
  }

  const onBlurValidateIdValue = async (event) => {
    // This is for numeric values only because the user finishes
    // entering the number when he leaves the field
    event.preventDefault()
    const value = event.target.value
    const splitValue = value.split('.')
    const newPrefix = splitValue.length > 1 ? validatePrefix(splitValue[0]) : prefix
    const newInputId = splitValue.length > 1 ? splitValue[1] : value

    if (IS_PURE_NUMBER.test(newInputId)) {
      await processNumericIdOnBlur(newInputId, newPrefix, value)
      return
    }

    if (PREFIX_VALIDATOR.regEx?.test(newPrefix.concat(newInputId))) {
      await onChangeValidateIdValue(event)
      return
    }

    setIdValue(value)
    setValidLabel(splitValue.length > 1 ? false : null)
  }

  const getIdLabel = () => {
    let label = ''
    if (validLabel === true) {
      label = side.label
    } else if (validLabel === false) {
      label = t('person.relationships.invalidId', 'Invalid ID')
    } else {
      label =
        side.side === 'left'
          ? t('person.relationships.sourceId', 'Source ID')
          : t('person.relationships.destinationId', 'Destination ID')
    }
    return label
  }

  return (
    <Row>
      <Select
        id={`select_${side.side}`}
        options={PREFIX_OPTIONS.map((option) => ({
          label: option.label,
          value: option.value,
        }))}
        onChange={(event) => {
          setPrefix(event.target.value)
          setIdValue('')
          setValidLabel(null)
        }}
        value={prefix}
        label={
          side.side === 'left'
            ? t('person.relationships.sourceIdType', 'Source ID Type')
            : t('person.relationships.destinationIdType', 'Destination ID Type')
        }
      />

      <TextField
        id={`input_${side.side}`}
        value={idValue}
        label={getIdLabel()}
        ref={idValueRef}
        autoFocus
        onChange={onChangeValidateIdValue}
        onClick={onClickHandler}
        onBlur={onBlurValidateIdValue}
        onEnterKey={onBlurValidateIdValue}
        clearable
        onClear={() => {
          setIdValue('')
          setValidLabel(null)
        }}
      />
    </Row>
  )
}

export default SelectId
